import './Header.scss';

import NavBar from '@hulu-react-style-components/nav-bar';
import envConfig from '@hulu/env-config';
import { NavLink, useLocation, useNavigate } from '@hulu/react-router-dom';
import type { GeneratedPermissions, NotificationsProps } from 'mission-control-common-components';
import { Notifications, User } from 'mission-control-common-components';
import React, { useCallback, useMemo, useState } from 'react';

import type { PermissionsNames } from '../../constants';
import { PathName } from '../../constants';
import { useCognitoDetails } from '../../hooks/useCognitoDetails';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import bem from '../../utils/bem';
// TODO(AX-1460): add this back when we're ready for global Elasticsearch
//import SearchBar from '../SearchBar';
import { AuthUserNav } from '../AuthUserNav';
import BasicSelect from '../BasicSelect';
import NavigationSelectOptions from '../NavigationSelectOptions';
import { forbidWhenNoPermission } from './utils';

export interface HeaderProps {
  isAuthEnabled?: boolean;
  permissions: GeneratedPermissions<PermissionsNames> | null;
  notificationsProp?: NotificationsProps;
}

const [block, element] = bem('header');
const [userModalBlock, userModalElement] = bem('custom_user_modal');

export type NavigationItem = {
  id: string;
  isDisabled?: boolean;
  label: string;
  isSelected?: boolean;
  pathName: string;
  hasPermission: boolean;
};

export type NavigationItemsGroup = {
  isActive?: boolean;
  isDisabled?: boolean;
  label: string;
  items: NavigationItem[];
};

const Header = ({ isAuthEnabled, permissions, notificationsProp }: HeaderProps): React.ReactElement => {
  const [widthOfModal, setWidthOfModal] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const cognitoDetails = useCognitoDetails();
  const { adsVsmCampaignReviewUi, adsMcMarkerCurationEnabled } = useFeatureFlags();

  const logout = (): void => {
    if (isAuthEnabled) {
      User.logout(cognitoDetails);
      return;
    }

    navigate('/logout');
  };

  const navItemsGroups = useMemo<Array<NavigationItemsGroup | NavigationItem>>(() => {
    const groups: Array<NavigationItemsGroup | NavigationItem> = [
      {
        label: 'Campaign Setup & Review',
        isDisabled: false,
        items: [
          {
            id: 'Trafficking',
            label: 'Trafficking',
            pathName: PathName.trafficking,
            hasPermission: !!permissions?.traffickingPageRead,
          },
          {
            id: 'Campaign-Review',
            label: 'Campaign Review',
            pathName: adsVsmCampaignReviewUi ? PathName.campaignReviewer : PathName.campaignReview,
            hasPermission: !!permissions?.campaignReviewPageRead,
          },
          {
            id: 'Asset-Review',
            label: 'Asset Review',
            pathName: PathName.asset2Review,
            hasPermission: !!permissions?.assetReviewPageRead,
          },
        ],
      },
      {
        label: 'Inventory',
        isDisabled: false,
        items: [
          {
            id: 'Ad-Load-Manager',
            label: 'Ad Load Manager',
            pathName: PathName.adLoadManager,
            hasPermission: !!permissions?.inventoryPageRead,
          },
          ...(adsMcMarkerCurationEnabled
            ? [
                {
                  id: 'Marker-Curation',
                  label: 'Marker Curation',
                  pathName: PathName.markerCuration,
                  hasPermission: !!permissions?.inventoryPageRead,
                },
              ]
            : []),
          {
            id: 'Inventory-Restriction-Manager',
            label: 'Inventory Restriction Manager',
            pathName: PathName.inventoryRestrictionManager,
            hasPermission: !!permissions?.inventoryPageRead,
          },
          ...(envConfig.REACT_APP_FEATURE_ENABLE_TARGETING_PRESETS === 'true'
            ? [
                {
                  id: 'Targeting-Presets',
                  label: 'Targeting Presets',
                  pathName: PathName.targetingPreset,
                  hasPermission: !!permissions?.presetPageRead,
                },
              ]
            : []),
          ...(envConfig.REACT_APP_FEATURE_ENABLE_INVENTORY_BLOCK === 'true'
            ? [
                {
                  id: 'Inventory-Block',
                  label: 'Inventory Block',
                  pathName: PathName.inventoryBlock,
                  hasPermission: !!permissions?.readInventoryBlock,
                },
              ]
            : []),
        ],
      },
      {
        label: 'Programmatic',
        isDisabled:
          envConfig.REACT_APP_ENABLE_DEMAND_MANAGEMENT_TAB !== 'true' &&
          envConfig.REACT_APP_ENABLE_SUPPLY_MANAGEMENT_TAB !== 'true',
        items: [
          {
            id: 'Demand-Management',
            label: 'Demand Management',
            isDisabled: envConfig.REACT_APP_ENABLE_DEMAND_MANAGEMENT_TAB !== 'true',
            pathName: PathName.demandManagement,
            hasPermission: !!permissions?.demandManagementPageRead,
          },
          {
            id: 'Supply-Management',
            label: 'Supply Management',
            pathName: PathName.supplyManagement,
            isDisabled: envConfig.REACT_APP_ENABLE_SUPPLY_MANAGEMENT_TAB !== 'true',
            hasPermission: !!permissions?.supplyManagementPageRead,
          },
        ],
      },
      {
        label: 'Finance & Pricing',
        items: [
          {
            id: 'Pricing',
            label: 'Pricing',
            pathName: PathName.pricing,
            hasPermission: !!permissions?.pricingPageRead,
          },
        ],
      },
      {
        id: 'contentTools',
        label: 'Content Search',
        pathName: PathName.contentMetaData,
        hasPermission: !!permissions?.traffickingPageRead,
      },
    ];

    return groups
      .filter((navOption) => !navOption.isDisabled)
      .map((navOption) => {
        if ('items' in navOption) {
          return {
            ...navOption,
            isActive: navOption.items.some((item) => location.pathname.includes(item.pathName)),
            items: navOption.items.map((item) => ({
              ...item,
              isSelected: location.pathname.includes(item.pathName),
            })),
          };
        }

        return {
          ...navOption,
          isSelected: location.pathname.includes(navOption.pathName),
        };
      });
  }, [location.pathname, permissions, adsVsmCampaignReviewUi, adsMcMarkerCurationEnabled]);

  const getNavigationOptionsComponent = useCallback(
    (items: NavigationItem[]) => (toggleOpen: () => void): JSX.Element => {
      return <NavigationSelectOptions toggleOpen={toggleOpen} options={items} />;
    },
    []
  );

  const menuLinks: JSX.Element = (
    <div className={element('menu')}>
      {navItemsGroups.map((navItem) => {
        if ('items' in navItem) {
          return (
            <div key={navItem.label} className={element('menu-item')}>
              <BasicSelect
                isActive={navItem.isActive}
                optionsComponent={getNavigationOptionsComponent(navItem.items)}
                overlayClassName={`${block()} ${element('navigation-tooltip')}`}
                buttonText={navItem.label}
              />
            </div>
          );
        }

        return (
          <NavLink
            key={navItem.label}
            className={element('option', navItem.isSelected ? 'selected' : '')}
            data-testid={`navigation-select-option-${navItem.id}`}
            to={navItem.pathName}
            onClick={forbidWhenNoPermission(navItem.hasPermission)}
          >
            {navItem.label}
          </NavLink>
        );
      })}
    </div>
  );

  const accessManagerComponent = useMemo(
    () => (
      <NavLink
        className={userModalElement('link')}
        data-testid="access-manager"
        to={PathName.accessManager}
        onClick={forbidWhenNoPermission(permissions?.accessManagerPageRead)}
      >
        Access Manager
      </NavLink>
    ),
    // Memoized as forbidWhenNoPermission can throw forbidden when component is inserted dynamically
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <>
      <NavBar
        className={block()}
        appName="Mission Control"
        // TODO
        // leftChildren: app sidenav bar
        // rightChildren[0]: search, notifications, and grid-menu
        leftChildren={null}
        rightChildren={[
          menuLinks,
          // TODO(AX-1460): add this back when we're ready for global Elasticsearch and remove "null"
          null,
          // // <SearchBar />,
          notificationsProp ? <Notifications key="NavNotifications" {...notificationsProp} /> : null,
          <AuthUserNav
            isAuthEnabled={isAuthEnabled}
            setIsOpen={setIsOpen}
            isOpen={isOpen}
            setWidthOfModal={setWidthOfModal}
          />,
        ]}
        logo={null}
      />
      {isOpen && (
        <div className={userModalBlock()} style={{ width: widthOfModal ?? 0 }} onClick={(): void => setIsOpen(false)}>
          {accessManagerComponent}
          <div onClick={logout} className={userModalElement('logout')}>
            Logout
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
